"use client";

import { useLanguage } from "@/contexts/LanguageContext";

export default function SectionBlueprints() {
  const { t } = useLanguage();

  return (
    <section className="bg-surface py-32 px-6 md:px-12 text-text-primary relative border-t border-border-default">
      <div className="max-w-5xl mx-auto relative z-10 text-center">
        <span className="inline-block py-1 px-3 rounded-full bg-brand-secondary/10 text-brand-secondary text-sm font-mono tracking-wider mb-8 border border-brand-secondary/20">
          {t("sectionBlueprints.badge")}
        </span>

        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-8">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-secondary to-brand-primary">
            {t("sectionBlueprints.title.highlight")}
          </span>{" "}
          {t("sectionBlueprints.title.after")}
        </h2>

        <p className="text-text-secondary text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-16">
          {t("sectionBlueprints.subtitle")}
        </p>

        {/* Blueprints Cards Visual */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
          <div className="bg-surface-card p-1 rounded-2xl border border-border-hover hover:-translate-y-2 transition-transform duration-300 shadow-2xl shadow-black/50">
            <div className="bg-surface-elevated p-6 rounded-xl h-full flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                    role="img"
                    aria-labelledby="icon-k8s"
                  >
                    <title id="icon-k8s">
                      {t("sectionBlueprints.cards.0.iconTitle")}
                    </title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.34-7.252 3 3 0 0 0-4.57-2.906 3.75 3.75 0 1 1-6.19-2.906A4.5 4.5 0 0 0 2.25 15Zm15 5.25a5.23 5.23 0 0 0 1.396-.194l-2.07-2.07a3.375 3.375 0 0 1-3.66 3.66l-2.05-2.05a4.883 4.883 0 0 0 2.766 1.154h3.618Z"
                    />
                  </svg>
                </div>
                <span className="text-xs font-mono text-text-secondary border border-border-hover px-2 py-1 rounded">
                  v2.4
                </span>
              </div>
              <h3 className="text-text-primary font-bold text-lg mb-2">
                {t("sectionBlueprints.cards.0.title")}
              </h3>
              <p className="text-text-secondary text-sm mb-4 flex-grow">
                {t("sectionBlueprints.cards.0.desc")}
              </p>
              <div className="w-full bg-blue-500/20 h-1 rounded-full overflow-hidden">
                <div className="w-full h-full bg-blue-500" />
              </div>
            </div>
          </div>

          <div className="bg-surface-card p-1 rounded-2xl border border-border-hover hover:-translate-y-2 transition-transform duration-300 shadow-2xl shadow-black/50 relative z-10 md:-mt-8">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-primary text-text-inverted text-[10px] uppercase font-bold px-3 py-1 rounded-full shadow-lg border border-brand-primary/50">
              {t("sectionBlueprints.mostPopular")}
            </div>
            <div className="bg-surface-elevated p-6 rounded-xl h-full flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-brand-primary/20 rounded-lg text-brand-primary">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                    role="img"
                    aria-labelledby="icon-secure-gitops"
                  >
                    <title id="icon-secure-gitops">
                      {t("sectionBlueprints.cards.1.iconTitle")}
                    </title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
                    />
                  </svg>
                </div>
                <span className="text-xs font-mono text-text-secondary border border-border-hover px-2 py-1 rounded">
                  v3.1
                </span>
              </div>
              <h3 className="text-text-primary font-bold text-lg mb-2">
                {t("sectionBlueprints.cards.1.title")}
              </h3>
              <p className="text-text-secondary text-sm mb-4 flex-grow">
                {t("sectionBlueprints.cards.1.desc")}
              </p>
              <div className="w-full bg-brand-primary/20 h-1 rounded-full overflow-hidden">
                <div className="w-full h-full bg-brand-primary" />
              </div>
            </div>
          </div>

          <div className="bg-surface-card p-1 rounded-2xl border border-border-hover hover:-translate-y-2 transition-transform duration-300 shadow-2xl shadow-black/50">
            <div className="bg-surface-elevated p-6 rounded-xl h-full flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                    role="img"
                    aria-labelledby="icon-observability"
                  >
                    <title id="icon-observability">
                      {t("sectionBlueprints.cards.2.iconTitle")}
                    </title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6"
                    />
                  </svg>
                </div>
                <span className="text-xs font-mono text-text-secondary border border-border-hover px-2 py-1 rounded">
                  v1.8
                </span>
              </div>
              <h3 className="text-text-primary font-bold text-lg mb-2">
                {t("sectionBlueprints.cards.2.title")}
              </h3>
              <p className="text-text-secondary text-sm mb-4 flex-grow">
                {t("sectionBlueprints.cards.2.desc")}
              </p>
              <div className="w-full bg-purple-500/20 h-1 rounded-full overflow-hidden">
                <div className="w-full h-full bg-purple-500" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
